/**
 * History page — unified timeline of all jobs, experiments, and conversations.
 * Sprint B2 stub: basic structure + real data from existing job APIs.
 */

import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";

interface HistoryItem {
  id: string;
  type: "experiment" | "mmm" | "bid" | "conversation";
  name: string;
  status: string;
  createdAt: string;
  result?: string;
}

const TYPE_ICONS: Record<string, string> = {
  experiment: "🧪",
  mmm: "📈",
  bid: "🎯",
  conversation: "💬",
};

const STATUS_COLORS: Record<string, string> = {
  success: "var(--success)",
  successful: "var(--success)",
  failed: "var(--error)",
  running: "var(--warning)",
  pending: "var(--text-tertiary)",
};

export default function HistoryPage() {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [filter, setFilter] = useState<"all" | "experiment" | "mmm" | "bid">("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Load from existing job run APIs
        const [geoRes, mmmRes, bidRes] = await Promise.allSettled([
          axiosInstance.get("/api/jobRun/jobType/GEO_LIFT"),
          axiosInstance.get("/api/jobRun/jobType/MMM"),
          axiosInstance.get("/api/jobRun/jobType/BidOptimizer"),
        ]);

        const combined: HistoryItem[] = [];

        if (geoRes.status === "fulfilled") {
          const data = Array.isArray(geoRes.value.data) ? geoRes.value.data : geoRes.value.data?.items || [];
          data.slice(0, 20).forEach((j: any) => combined.push({
            id: j.jobRunId || j.jobConfigId,
            type: "experiment",
            name: j.jobName || j.name || "Geo Lift Study",
            status: (j.jobStatus || j.status || "").toLowerCase(),
            createdAt: j.createdDate || j.createdAt || "",
            result: j.aggregateLift ? `+${j.aggregateLift.toFixed(1)}% lift` : undefined,
          }));
        }
        if (mmmRes.status === "fulfilled") {
          const data = Array.isArray(mmmRes.value.data) ? mmmRes.value.data : mmmRes.value.data?.items || [];
          data.slice(0, 10).forEach((j: any) => combined.push({
            id: j.jobRunId || j.jobConfigId,
            type: "mmm",
            name: j.jobName || j.name || "MMM Run",
            status: (j.jobStatus || j.status || "").toLowerCase(),
            createdAt: j.createdDate || j.createdAt || "",
          }));
        }
        if (bidRes.status === "fulfilled") {
          const data = Array.isArray(bidRes.value.data) ? bidRes.value.data : bidRes.value.data?.items || [];
          data.slice(0, 10).forEach((j: any) => combined.push({
            id: j.jobRunId || j.jobConfigId,
            type: "bid",
            name: j.jobName || j.name || "Bid Optimizer Run",
            status: (j.jobStatus || j.status || "").toLowerCase(),
            createdAt: j.createdDate || j.createdAt || "",
          }));
        }

        // Sort by newest first
        combined.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setItems(combined);
      } catch { /* silent */ }
      setLoading(false);
    };
    load();
  }, []);

  const displayed = filter === "all" ? items : items.filter(i => i.type === filter);

  return (
    <div style={{ padding: 32, maxWidth: 860, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: "var(--text-primary)", margin: 0, fontFamily: "var(--font-sans)" }}>
          Past Work
        </h1>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 4, fontFamily: "var(--font-sans)" }}>
          All your experiments, model runs, and analysis history
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {(["all", "experiment", "mmm", "bid"] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "5px 12px",
              borderRadius: 20,
              fontSize: 12,
              fontFamily: "var(--font-sans)",
              fontWeight: filter === f ? 500 : 400,
              background: filter === f ? "var(--blue-subtle)" : "var(--bg-surface)",
              color: filter === f ? "var(--blue)" : "var(--text-secondary)",
              border: `1px solid ${filter === f ? "var(--blue)" : "var(--border-default)"}`,
              cursor: "pointer",
              transition: "all 150ms ease",
            }}
          >
            {f === "all" ? "All" : f === "experiment" ? "🧪 Experiments" : f === "mmm" ? "📈 MMM" : "🎯 Bid Optimizer"}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{ height: 64, borderRadius: "var(--radius)", background: "var(--bg-surface)", animation: "pulse 1.5s ease infinite" }} />
          ))}
        </div>
      ) : displayed.length === 0 ? (
        <div style={{ textAlign: "center", padding: "64px 24px" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <p style={{ color: "var(--text-secondary)", fontSize: 14, fontFamily: "var(--font-sans)" }}>No history yet for this filter</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {displayed.map(item => (
            <div
              key={item.id}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "12px 16px",
                background: "var(--bg-surface)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius)",
                transition: "border-color 150ms ease",
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--border-default)")}
              onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--border-subtle)")}
            >
              <span style={{ fontSize: 18, width: 28, textAlign: "center", flexShrink: 0 }}>
                {TYPE_ICONS[item.type]}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, color: "var(--text-primary)", fontFamily: "var(--font-sans)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {item.name}
                </div>
                <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginTop: 2, fontFamily: "var(--font-sans)" }}>
                  {item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                </div>
              </div>
              {item.result && (
                <div style={{ fontSize: 12, color: "var(--success)", fontFamily: "var(--font-mono)", fontWeight: 500, flexShrink: 0 }}>
                  {item.result}
                </div>
              )}
              <div style={{
                display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
                fontSize: 11, color: STATUS_COLORS[item.status] || "var(--text-tertiary)",
                fontFamily: "var(--font-sans)",
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: STATUS_COLORS[item.status] || "var(--border-default)",
                  flexShrink: 0,
                }} />
                <span style={{ textTransform: "capitalize" }}>{item.status || "Unknown"}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
